import { useRef, useState } from "react";
import { parseCsvFile } from "@/lib/csv";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Download, X, CheckCircle2, AlertTriangle } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const TEMPLATE = "categoria,ronda,fecha,hora,hoyo\nPremier,1,2026-07-17,08:00,1\nPremier,1,2026-07-17,08:10,1\nPremier,1,2026-07-17,08:20,10\nPrimera,1,2026-07-17,08:00,1\nPrimera,1,2026-07-17,08:10,1\n";

// Accent-insensitive header → canonical field map
const stripAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const HEADER_MAP = {
  categoria: "category", category: "category", cat: "category",
  ronda: "round", round: "round", rnd: "round",
  fecha: "date", date: "date", dia: "date",
  hora: "time", time: "time", horario: "time", salida: "time", hr: "time",
  horainicio: "start_time", inicio: "start_time", start: "start_time", starttime: "start_time", horastart: "start_time",
  horafin: "end_time", fin: "end_time", end: "end_time", endtime: "end_time", horaend: "end_time",
  hoyo: "hole", hole: "hole", teebox: "hole", tee: "hole",
};

function normHeader(h) {
  const key = stripAccents((h || "").toLowerCase()).replace(/[\s_]+/g, "");
  return HEADER_MAP[key] || null;
}

export default function CalendarCsvImport({ tid, categories, onImported }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [applying, setApplying] = useState(false);

  const catNames = new Set((categories || []).map((c) => (c.name || "").trim().toLowerCase()));

  const parseFile = (file) => {
    if (!file) return;
    parseCsvFile(file, { transformHeader: (h) => normHeader(h) || h }).then((res) => {
        const parsed = (res.data || []).map((r, i) => {
          const category = (r.category || "").trim();
          const round = (r.round || "").toString().trim();
          const date = (r.date || "").trim();
          const time = (r.time || "").trim();
          const start_time = (r.start_time || "").trim();
          const end_time = (r.end_time || "").trim();
          const hole = (r.hole || "").toString().trim();
          const matched = catNames.has(category.toLowerCase());
          return { _i: i, category, round, date, time, start_time, end_time, hole, matched };
        }).filter((r) => r.category || r.date);
        if (parsed.length === 0) { toast.error("No se encontraron filas válidas en el CSV"); return; }
        setRows(parsed);
      }).catch(() => toast.error("No se pudo leer el CSV"));
  };

  const apply = async () => {
    setApplying(true);
    try {
      const payload = {
        rows: rows.map((r) => ({
          category: r.category, round: r.round, date: r.date,
          time: r.time, start_time: r.start_time, end_time: r.end_time, hole: r.hole,
        })),
      };
      const res = await fetch(`${API}/club/tournaments/${tid}/convocatoria/calendar-import`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`Calendario reemplazado: ${data.applied} salida(s) aplicadas${data.created_slots ? ` · ${data.created_slots} horario(s) nuevo(s) creados` : ""}`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} fila(s) omitidas: ${data.skipped.slice(0, 3).map((s) => s.reason).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
      if (data.no_slots?.length) toast.warning(`${data.no_slots.length} rango(s) sin salidas — usa la columna "hora" para subir horarios exactos.`);
      setRows(null);
      onImported?.(data.tournament);
    } catch (e) { toast.error(e.message); }
    finally { setApplying(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla_calendario.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const unmatched = (rows || []).filter((r) => !r.matched).length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
        onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="calendar-csv-input" />
      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="calendar-csv-btn">
        <Upload className="w-3.5 h-3.5 mr-1" /> Importar CSV
      </Button>
      <button onClick={downloadTemplate} className="text-xs text-[#1B4D3E] font-medium flex items-center gap-1 hover:underline" data-testid="calendar-csv-template">
        <Download className="w-3.5 h-3.5" /> Plantilla
      </button>

      {rows && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="calendar-csv-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-bold text-[#0F172A]">Vista previa del calendario</h3>
              <button onClick={() => setRows(null)} data-testid="calendar-csv-close"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="px-5 py-3 overflow-auto flex-1">
              <div className="flex items-center gap-2 mb-3 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Reemplaza TODO el calendario actual
                </span>
                <span className="text-[#64748B]">{rows.length} fila(s){unmatched > 0 && <span className="text-red-600"> · {unmatched} sin categoría válida</span>}</span>
              </div>
              <table className="w-full text-sm" data-testid="calendar-csv-preview-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-[#64748B] border-b">
                    <th className="py-1.5">Categoría</th><th>Ronda</th><th>Fecha</th><th>Hora</th><th>Hoyo</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r._i} className={`border-b last:border-b-0 ${r.matched ? "" : "bg-red-50"}`} data-testid={`calendar-csv-row-${r._i}`}>
                      <td className="py-1.5">{r.category || <span className="text-red-500">—</span>}</td>
                      <td>{r.round}</td><td>{r.date}</td>
                      <td>{r.time || (r.start_time ? `${r.start_time}${r.end_time ? `–${r.end_time}` : ""}` : "—")}</td>
                      <td>{r.hole ? `H${r.hole}` : (r.time ? "H1" : "1 y 10")}</td>
                      <td>{r.matched ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <span className="text-[11px] text-red-600">categoría ?</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <Button size="sm" variant="outline" onClick={() => setRows(null)} data-testid="calendar-csv-cancel">Cancelar</Button>
              <Button size="sm" onClick={apply} disabled={applying} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="calendar-csv-apply">
                {applying ? "Aplicando..." : `Aplicar y reemplazar`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
